import './inviteLinksWidget.scss'

import {
    AnalyticsSelectors,
    CreateChannelInviteLink,
    InviteLinksChannelsTable,
    InviteLinksChart,
} from '@/features/analytics'
import { useInviteLinksTimeSelectors } from '@/features/analytics/invite-links/hooks'
import { isMasterLink } from '@/features/analytics/invite-links/ui/InviteLinksTable/shared/masterLink'
import { useInviteLinksStore } from '@/features/analytics/model'
import {
    InviteLinkWithAnalyticsType,
    useCreativesPrefetchQuery,
} from '@/shared/api'
import { Title } from '@/shared/ui'
import {
    workspacePath,
    AdvertiserAccount,
    Channel,
    useActiveWorkspace,
} from '@/true-fsd/data/advertiser-account'
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router'
import { useGroupNameChannels } from '../hooks/useGroupName'
import { useChannelLinksByGroup } from '@/true-fsd/data/link'

export type InviteLinksChannelsWidgetProps = {
    groupId: number
    activeChannel: Channel
    activeAccount: AdvertiserAccount | null
}
export const InviteLinksChannelsWidget = ({
    groupId,
    activeChannel,
    activeAccount,
}: InviteLinksChannelsWidgetProps) => {
    useCreativesPrefetchQuery({
        workspace_type: 'channel',
        workspace_id: activeChannel.channel_id,
    })

    const groupName = useGroupNameChannels({
        channelId: activeChannel.channel_id,
    })

    const { data: inviteLinks = [], isLoading: isTableLoading } =
        useChannelLinksByGroup({
            channel_id: activeChannel.channel_id,
            channel_links_group_id: groupId,
            filter: groupName !== 'Все ссылки',
        })

    const [optimisticInviteLinks, setOptimisticInviteLinks] = useState<
        InviteLinkWithAnalyticsType[]
    >([])

    const { movedGroups } = useInviteLinksStore()

    const onInviteLinkCreated = (newLink: InviteLinkWithAnalyticsType) => {
        setOptimisticInviteLinks((prev) => [...prev, newLink])
    }

    const onRemoveInviteLink = (newLink: InviteLinkWithAnalyticsType) => {
        const newLinkCreatedAt = new Date(newLink.created_at)
        setOptimisticInviteLinks((prev) =>
            prev.filter(
                (link) =>
                    !(
                        link.invite_link_id === 0 &&
                        link.invite_link_name === newLink.invite_link_name &&
                        link.budget === newLink.budget &&
                        link.created_at.getHours() ===
                            newLinkCreatedAt.getHours() &&
                        link.created_at.getDate() ===
                            newLinkCreatedAt.getDate() &&
                        link.created_at.getFullYear() ===
                            newLinkCreatedAt.getFullYear() &&
                        link.created_at.getMonth() ===
                            newLinkCreatedAt.getMonth()
                    )
            )
        )
    }

    const onInviteLinkUpdated = (updatedLink: InviteLinkWithAnalyticsType) => {
        setOptimisticInviteLinks((prev) => {
            const updatedLinkCreatedAt = new Date(updatedLink.created_at)
            return prev.map((link) =>
                link.invite_link_id === 0 &&
                link.invite_link_name === updatedLink.invite_link_name &&
                link.budget === updatedLink.budget &&
                link.created_at.getHours() ===
                    updatedLinkCreatedAt.getHours() &&
                link.created_at.getDate() === updatedLinkCreatedAt.getDate() &&
                link.created_at.getFullYear() ===
                    updatedLinkCreatedAt.getFullYear() &&
                link.created_at.getMonth() === updatedLinkCreatedAt.getMonth()
                    ? { ...updatedLink } //updatedLink // Replace the temporary row with the finalized row
                    : link
            )
        })
    }

    const combinedInviteLinks = useMemo(() => {
        // Use a Map to deduplicate rows based on their IDs
        const linkMap: Record<number, InviteLinkWithAnalyticsType> = {}
        // Add backend-fetched invite links
        ;(inviteLinks || []).forEach((link) => {
            linkMap[link.invite_link_id] = link
        })

        // Add optimistic invite links, overriding any duplicates
        optimisticInviteLinks.forEach((link) => {
            if (
                !movedGroups.some((movedGroup) =>
                    movedGroup.inviteLinks.some(
                        (movedLink) =>
                            movedLink.invite_link_id === link.invite_link_id &&
                            movedGroup.groupId !== link.invite_links_group_id
                    )
                )
            ) {
                //override only if optimistic link is more up-to-date
                if (
                    new Date(link.updated_at).getTime() >
                    (linkMap[link.invite_link_id]
                        ? new Date(
                              linkMap[link.invite_link_id].updated_at
                          ).getTime()
                        : 0)
                ) {
                    linkMap[link.invite_link_id] = link
                }
            }
        })

        // // Add optimistic invite links, overriding duplicates only if necessary
        // optimisticInviteLinks.forEach((optimisticLink) => {
        //     const existingLink = linkMap[optimisticLink.invite_link_id];
        //     // Replace the existing link if:
        //     // 1. The optimistic link has a temporary ID (newly added row).
        //     // 2. The optimistic link has been modified (e.g., updated budget).
        //     if (
        //         !existingLink ||
        //         optimisticLink.invite_link_id === 0 || // Temporary ID for new rows
        //         JSON.stringify(optimisticLink) !== JSON.stringify(existingLink)
        //     ) {
        //         console.log("existingLink", existingLink, optimisticLink)
        //         linkMap[optimisticLink.invite_link_id] = optimisticLink;
        //     }
        // });
        return Object.values(linkMap)
    }, [inviteLinks, optimisticInviteLinks, movedGroups])

    const {
        startDateInvite,
        endDateInvite,
        usersSortInvite,
        subscriberStatusSortInvite,
        detalizationSortInvite,
        lineDateInvite,
        selectedLinksInviteByGroup,
        toggleSelectedLinkInviteByGroup,
        setLineDateInvite,
        setStartDateInvite,
        setEndDateInvite,
        setDetalizationSortInvite,
        setUsersSortInvite,
        setSubscriberStatusSortInvite,
    } = useInviteLinksStore()
    const { handleLineDateSelect, handleDateChange } =
        useInviteLinksTimeSelectors({
            setLineDate: setLineDateInvite,
            setStartDate: setStartDateInvite,
            setEndDate: setEndDateInvite,
        })

    const [initialSelectionChecked, setInitialSelectionChecked] =
        useState(false)

    useEffect(() => {
        async function selectInitialLinks() {
            const selectedLinks = selectedLinksInviteByGroup[groupId] || []

            if (selectedLinks.length > 0 || initialSelectionChecked) {
                setInitialSelectionChecked(true)
                return
            }

            const lastVisit = localStorage.getItem('lastVisit')
            const now = new Date().getTime()
            let shouldSelectLinks = false

            if (lastVisit) {
                const lastVisitDate = new Date(lastVisit).getTime()
                const timeDifference = (now - lastVisitDate) / (1000 * 60 * 60)
                if (timeDifference > 1) {
                    shouldSelectLinks = true
                }
            } else {
                shouldSelectLinks = true
            }

            if (shouldSelectLinks) {
                if (inviteLinks && inviteLinks.length > 0) {
                    const firstThreeLinks = inviteLinks
                        .sort(
                            (a, b) =>
                                new Date(b.created_at).getTime() -
                                new Date(a.created_at).getTime()
                        )
                        .slice(0, 3)
                    firstThreeLinks.forEach((link) => {
                        toggleSelectedLinkInviteByGroup(groupId, link)
                    })
                    setInitialSelectionChecked(true)
                }
            }

            localStorage.setItem('lastVisit', new Date().toISOString())
        }

        void selectInitialLinks()
    }, [
        inviteLinks,
        activeChannel,
        selectedLinksInviteByGroup,
        initialSelectionChecked,
        toggleSelectedLinkInviteByGroup,
        groupId,
    ])

    const { activeWorkspace } = useActiveWorkspace()

    return (
        <div className="invite-links-groups-widget">
            <div>
                <Title className="title">Ссылки</Title>
                <p className="invite-links-groups-widget__link">
                    <Link
                        to={workspacePath(activeWorkspace, 'analytics/links')}
                        className="campaigns-link"
                    >
                        Группы
                    </Link>{' '}
                    / {groupName}
                </p>
            </div>
            <section className="invite-links-widget">
                <AnalyticsSelectors
                    lineDate={lineDateInvite}
                    detalization={detalizationSortInvite}
                    users={usersSortInvite}
                    subscribers={subscriberStatusSortInvite}
                    datePicker={{
                        startDate: startDateInvite,
                        endDate: endDateInvite,
                    }}
                    onLineDateSelect={handleLineDateSelect}
                    onDetalizationSelect={setDetalizationSortInvite}
                    onUsersSelect={setUsersSortInvite}
                    onSubscribersSelect={setSubscriberStatusSortInvite}
                    onDateChange={{
                        onFromDateChange: (date) =>
                            handleDateChange(date, endDateInvite),
                        onToDateChange: (date) =>
                            handleDateChange(startDateInvite, date),
                    }}
                    mode="InviteLinks"
                />

                <InviteLinksChart activeChannel={activeChannel} />
                {!isMasterLink(groupName) && (
                    <CreateChannelInviteLink
                        activeChannel={activeChannel}
                        onInviteLinkCreated={onInviteLinkCreated}
                        onInviteLinkUpdated={onInviteLinkUpdated}
                        onRemoveInviteLink={onRemoveInviteLink}
                    />
                )}

                <InviteLinksChannelsTable
                    activeChannel={activeChannel}
                    activeAccount={activeAccount}
                    groupId={groupId}
                    inviteLinks={combinedInviteLinks}
                    isTableLoading={isTableLoading}
                />
            </section>
        </div>
    )
}
